import React, { ChangeEvent } from 'react'

import clsx from 'clsx'

import { WidgetChoice } from '@/common/util/choice'

import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'

export const SelectorSize = {
  AUTO: 'auto',
  SMALL: 'small',
  X_MEDIUM: 'x-medium',
  MEDIUM: 'medium',
  REMNANT: 'remnant'
} as const

export type SelectorSize = (typeof SelectorSize)[keyof typeof SelectorSize]

export const SelectorType = {
  DEFAULT: 'default',
  SEMI_REQUIRED: 'semi-required',
  REQUIRED: 'required',
  SUCCESS: 'success',
  NEGATIVE: 'negative',
  CAUTION: 'caution',
  SECONDARY: 'secondary'
} as const

export type SelectorType = (typeof SelectorType)[keyof typeof SelectorType]

interface Props {
  size?: SelectorSize
  selectorType?: SelectorType
  name: string
  choices: WidgetChoice<string | number>[]
  value?: string | number
  error?: boolean
  required?: boolean
  disabled?: boolean
  onChange(e: ChangeEvent<HTMLSelectElement>): void
  inputRef?: React.LegacyRef<HTMLSelectElement>
}

const Selector = ({
  size = SelectorSize.SMALL,
  selectorType = SelectorType.DEFAULT,
  name,
  choices,
  value,
  error = false,
  required = false,
  disabled = false,
  onChange,
  inputRef
}: Props) => {
  const widthClass =
    size == SelectorSize.SMALL
      ? 'w-22 min-w-[88px]'
      : size == SelectorSize.X_MEDIUM
        ? 'w-30 min-w-[120px]'
        : size == SelectorSize.MEDIUM
          ? 'w-46  min-w-[184px]'
          : size == SelectorSize.REMNANT
            ? 'flex-1 min-w-0'
            : size == SelectorSize.AUTO
              ? 'w-fit'
              : 'w-auto'
  const textClass =
    selectorType == SelectorType.SUCCESS
      ? 'text-success'
      : selectorType == SelectorType.NEGATIVE
        ? 'text-critical'
        : selectorType == SelectorType.CAUTION
          ? 'text-caution'
          : 'text-primary'
  const borderClass =
    selectorType == SelectorType.SEMI_REQUIRED || selectorType == SelectorType.REQUIRED || error
      ? 'border-critical-secondary hover:border-critical-secondary border border-solid'
      : selectorType == SelectorType.SECONDARY
        ? 'border-none outline-none'
        : 'border-border-input hover:border-border-input-hover border border-solid'
  const bgClass =
    selectorType == SelectorType.REQUIRED || error
      ? 'bg-surface-critical'
      : selectorType == SelectorType.SUCCESS
        ? 'bg-fill-success-secondary'
        : selectorType == SelectorType.CAUTION
          ? 'bg-fill-caution-secondary'
          : selectorType == SelectorType.NEGATIVE || selectorType == SelectorType.SECONDARY
            ? 'bg-fill-transparent-secondary'
            : 'bg-input hover:bg-input-hover '
  return (
    <div className={clsx('relative inline-block max-h-8', widthClass)}>
      <select
        name={name}
        className={clsx(
          'shadow-inset outline-default text-size-13 active:border-border-input-active active:bg-input-active disabled:bg-surface-disabled disabled:text-disabled w-fit max-w-full min-w-full appearance-none rounded-lg py-1.5 pr-6 pl-3 disabled:pointer-events-none disabled:border-none',
          textClass,
          borderClass,
          bgClass
        )}
        aria-invalid='false'
        value={value}
        required={required}
        disabled={disabled}
        onChange={onChange}
        ref={inputRef}
      >
        {choices.map((choice: WidgetChoice<string | number>, index: number) => (
          <option key={index} value={choice.value} disabled={choice.disabled}>
            {choice.name}
          </option>
        ))}
      </select>
      <div className='pointer-events-none absolute inset-y-0 right-2 grid place-content-center'>
        <Icon color={disabled ? IconColor.DISABLED : IconColor.PRIMARY} type={IconType.PULL_DOWN} />
      </div>
    </div>
  )
}

export default Selector
